import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { SEO } from "../components/SEO";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CreditCard, Check, Loader2, ArrowRight, Ticket, X, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function SubscriptionPage() {
  const { user, token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const sessionId = searchParams.get("session_id");
  const basePrice = 2.50;

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch site settings:", error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const checkPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      // Poll for payment status
      for (let i = 0; i < 5; i++) {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          await refreshUser();
          toast.success("Welcome to All WHUFC! Your membership is now active.");
          navigate("/", { replace: true });
          return;
        }
        
        // Wait 2 seconds before next check
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      toast.info("Payment is being processed. Please refresh in a moment.");
    } catch (error) {
      toast.error("Failed to verify payment status");
    } finally {
      setCheckingPayment(false);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setValidatingCode(true);
    try {
      const response = await axios.post(`${API}/discount-codes/validate`, null, {
        params: { code: discountCode.trim().toUpperCase() }
      });
      setAppliedDiscount(response.data);
      toast.success(`Discount applied! ${response.data.discount_percent}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid discount code");
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
  };

  const handleSubscribe = async () => {
    if (!user) {
      navigate("/register");
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Check if it's a free subscription (payment disabled)
      if (response.data.free_subscription) {
        toast.success(response.data.message);
        await refreshUser();
        navigate("/", { replace: true });
        return;
      }

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate membership");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Access to 100+ years of fixture history",
    "Complete player database with detailed bios",
    "Exclusive fan forum - post and reply",
    "Cup competition archives and stats",
    "Entertainment section - books, films, podcasts",
    "Club history - 1000 words per year",
    "Priority access to new features",
    "Cancel anytime - no commitment"
  ];

  // If checking payment after redirect
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[#7C2C3B] mx-auto mb-6 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            ACTIVATING MEMBERSHIP
          </h1>
          <p className="text-slate-600">Please wait while we confirm your membership...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Already subscribed
  if (user?.is_subscribed) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500 mx-auto mb-6 flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            YOU'RE A MEMBER!
          </h1>
          <p className="text-slate-600 mb-6">
            Your membership is active until {new Date(user.subscription_end).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'long', year: 'numeric'
            })}
          </p>
          <Link to="/">
            <Button className="bg-[#7C2C3B] hover:bg-[#63232F]">
              EXPLORE THE APP <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Loading settings
  if (loadingSettings) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <Loader2 className="w-10 h-10 text-[#7C2C3B] animate-spin mx-auto" />
        </div>
        <Footer />
      </div>
    );
  }

  // Payment is disabled - show free membership activation
  if (siteSettings && !siteSettings.payment_enabled) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              JOIN THE <span className="text-[#7C2C3B]">WEST HAM FAMILY</span>
            </h1>
            <p className="text-xl text-slate-600">Unlimited access to everything a true Hammer needs</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Benefits */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                WHAT YOU GET
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#2BAEE5] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Free Membership Card */}
            <div className="bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2 opacity-10"></div>
              
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#F8D742] text-[#0F172A] px-3 py-1 rounded-full text-sm font-bold mb-6">
                  <Gift className="w-4 h-4" /> FREE ACCESS
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>FREE</span>
                  </div>
                  <p className="text-white/80 mt-2">
                    Full access to all features - no payment required!
                  </p>
                </div>

                <Button 
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 font-bold py-6 text-lg"
                  data-testid="subscribe-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ACTIVATING...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      {user ? "ACTIVATE MEMBERSHIP" : "SIGN UP TO JOIN"}
                    </>
                  )}
                </Button>

                <div className="mt-6 flex items-center justify-center gap-4 text-sm text-white/80">
                  <span className="flex items-center gap-1">
                    <Check className="w-4 h-4" /> No card required
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="w-4 h-4" /> Instant access
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              Questions? Contact us at infowhufc@gmail.com
            </p>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Payment is enabled - show normal subscription flow
  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
      <SEO title="Subscribe" path="/subscription" description="Join the West Ham Family. Get premium access to exclusive content, ad-free browsing and more for Hammers supporters." noindex />
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            JOIN THE <span className="text-[#7C2C3B]">WEST HAM FAMILY</span>
          </h1>
          <p className="text-xl text-slate-600">Unlimited access to everything a true Hammer needs</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Benefits */}
          <div className="bg-white rounded-2xl p-8 border border-slate-200">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              WHAT YOU GET
            </h2>
            <ul className="space-y-4">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#2BAEE5] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-slate-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pricing Card */}
          <div className="bg-[#7C2C3B] rounded-2xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#2BAEE5] rounded-full -translate-y-1/2 translate-x-1/2 opacity-20"></div>
            
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-[#F8D742] text-[#0F172A] px-3 py-1 rounded-full text-sm font-bold mb-6">
                {appliedDiscount ? `${appliedDiscount.discount_percent}% OFF` : "BEST VALUE"}
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  {appliedDiscount ? (
                    <>
                      <span className="text-3xl line-through text-white/50" style={{ fontFamily: 'Barlow Condensed' }}>£{basePrice.toFixed(2)}</span>
                      <span className="text-6xl font-bold text-[#F8D742]" style={{ fontFamily: 'Barlow Condensed' }}>
                        £{appliedDiscount.final_price.toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-6xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>£{basePrice.toFixed(2)}</span>
                  )}
                  <span className="text-xl text-white/70">/month</span>
                </div>
                <p className="text-white/80 mt-2">
                  {appliedDiscount 
                    ? `You save £${appliedDiscount.discount_amount.toFixed(2)} per month!`
                    : "Billed monthly. Cancel anytime."
                  }
                </p>
              </div>

              {/* Discount Code Input */}
              <div className="mb-6 bg-white/10 rounded-lg p-4">
                <label className="text-sm text-white/80 mb-2 block flex items-center gap-2">
                  <Ticket className="w-4 h-4" /> Have a discount code?
                </label>
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-green-500/20 border border-green-400/30 rounded-lg px-4 py-3">
                    <div>
                      <span className="font-mono font-bold text-lg">{appliedDiscount.code}</span>
                      <span className="ml-3 text-green-300 text-sm">{appliedDiscount.discount_percent}% discount applied</span>
                    </div>
                    <button 
                      onClick={removeDiscount}
                      className="text-white/60 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      maxLength={8}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/50 font-mono uppercase tracking-wider"
                      data-testid="discount-code-input"
                    />
                    <Button
                      onClick={validateDiscountCode}
                      disabled={validatingCode || discountCode.length !== 8}
                      variant="secondary"
                      className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                      data-testid="apply-discount-btn"
                    >
                      {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                )}
              </div>

              <Button 
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-[#F8D742] text-[#0F172A] hover:bg-yellow-300 font-bold py-6 text-lg"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    PROCESSING...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {user ? "JOIN NOW" : "SIGN UP & JOIN"}
                  </>
                )}
              </Button>

              <div className="mt-6 flex items-center justify-center gap-4 text-sm text-white/60">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4" /> Secure checkout
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4" /> Instant access
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            Questions? Contact us at infowhufc@gmail.com
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
